import { Box } from '@mui/material';
import {
  getAccountingPeriods,
  updateAccountingPeriod,
} from 'lib/api/accounting';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { AccountingPeriod } from '../../types';
import ClosePeriodDialog from './components/ClosePeriodDialog';

const Periods: React.FC = () => {
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [failedText, setFailedText] = useState<string | null>(null);
  const [successText, setSuccessText] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [periodToClose, setPeriodToClose] = useState<AccountingPeriod | null>(
    null,
  );
  const [closeDate, setCloseDate] = useState<string>(
    periodToClose?.endDate.split('T')[0] || '',
  );

  useEffect(() => {
    if (successText || failedText) {
      const timer = setTimeout(() => {
        setSuccessText(null);
        setFailedText(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successText, failedText]);

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        setLoading(true);
        const data = await getAccountingPeriods(dispatch);
        setPeriods(data);
      } catch (error) {
        console.error('Error fetching accounting periods:', error);
        setPeriods([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchPeriods();
  }, [dispatch]);

  const handleClosePeriod = (periodId: number) => {
    const period = periods.find((p) => p.id === periodId);
    if (period) {
      setPeriodToClose(period);
      setCloseDate(period.endDate.split('T')[0]);
      setConfirmModalOpen(true);
    }
  };

  const handleConfirmClosePeriod = async () => {
    if (!periodToClose) return;

    try {
      const updateData = {
        status: 'CLOSED',
        closedAt: new Date(closeDate),
        closedByUserId: user?.id,
        closeDate: new Date(closeDate),
      };

      await updateAccountingPeriod(dispatch, periodToClose.id, updateData);

      // Reload periods data
      const data = await getAccountingPeriods(dispatch);
      setPeriods(data);

      setSuccessText('Período cerrado exitosamente');
      setFailedText(null);
      setConfirmModalOpen(false);
      setPeriodToClose(null);
      setCloseDate('');
    } catch (error) {
      console.error('Error closing period:', error);
      setFailedText('Error al cerrar el período');
      setSuccessText(null);
    }
  };

  const handleReopenPeriod = async (periodId: number) => {
    try {
      const updateData = {
        status: 'OPEN',
        reopenedAt: new Date(),
        reopenedById: user?.id,
      };

      const updatedPeriod = await updateAccountingPeriod(
        dispatch,
        periodId,
        updateData,
      );

      setPeriods((prevPeriods) =>
        prevPeriods.map((period) =>
          period.id === updatedPeriod.id
            ? {
                ...updatedPeriod,
              }
            : period,
        ),
      );
      setSuccessText('Período reabierto exitosamente');
      setFailedText(null);
    } catch (error) {
      console.error('Error reopening period:', error);
      setFailedText('Error al reabrir el período');
      setSuccessText(null);
    }
  };

  const tableHeaders = [
    {
      key: 'label',
      label: 'Período',
      type: 'text',
    },
    {
      key: 'startDate',
      label: 'Fecha Inicio',
      type: 'date',
    },
    {
      key: 'endDate',
      label: 'Fecha Fin',
      type: 'date',
    },
    {
      key: 'translatedStatus',
      label: 'Estado',
      type: 'status-chip',
    },
    {
      key: 'closedAt',
      label: 'Cerrado el',
      type: 'date',
    },
    {
      key: 'closedByName',
      label: 'Cerrado por',
      type: 'text',
    },
  ];

  const actions = [
    {
      action: handleClosePeriod,
      actionLabel: 'Cerrar Período',
      canShow: (periodId: number) => {
        const period = periods.find((p) => p.id === periodId);
        return period?.status === 'OPEN';
      },
    },
    {
      action: handleReopenPeriod,
      actionLabel: 'Reabrir Período',
      canShow: (periodId: number) => {
        const period = periods.find((p) => p.id === periodId);
        return period?.status === 'CLOSED';
      },
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          padding: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CenteredCircularProgress type="layout" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      {failedText && (
        <AlertBanner title="Error" severity="error" message={failedText} />
      )}
      {successText && (
        <AlertBanner title="Éxito" severity="success" message={successText} />
      )}
      <EnhancedTable
        data={periods}
        dataHeaders={tableHeaders}
        actions={actions}
        caption="Gestión de períodos contables"
        showActions={true}
        rowsPerPageDefault={10}
        exportToFile="csv"
        fileName="periodos-contables"
      />

      <ClosePeriodDialog
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        periodToClose={periodToClose}
        closeDate={closeDate}
        onCloseDateChange={setCloseDate}
        onConfirm={() => void handleConfirmClosePeriod()}
      />
    </Box>
  );
};

export default Periods;
